import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  IconButton,
  Typography,
  Box,
  TextField,
  CircularProgress,
  Autocomplete,
  Card,
  CardMedia,
  CardContent,
  CardActionArea,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useState, useEffect, useMemo, useRef } from "react";
import { useNotify, Confirm, useRefresh } from "react-admin";
import {
  useManualRedemption,
  useRewardListQuery,
  resolveRewardSubscriber,
  ResolvedRewardSubscriber,
} from "../controller";
import { getRewardCategoriesForPlatform } from "src/lib/rewardCategoryMap";

// Shape from rewardList GraphQL
interface RewardItem {
  id: string;
  amount: {
    coin: number | null;
    point: number | null;
  } | null;
  category: string;
  image?: string | null;
  label: string | null;
  stock?: number | null;
  available?: boolean | null;
}

interface RedemptionItem {
  id: string;
  name: string;
  service: string;
  displayName: string;
  imageUrl: string;
  points: number;
  balance: number;
  stock: number | null;
  available: boolean | null;
}

type ResolutionState = "idle" | "loading" | "resolved" | "unresolved";

const PLATFORM_OPTIONS = [
  { id: "G2", name: "G2" },
  { id: "M21", name: "M21" },
  { id: "GHK", name: "GHK" },
];

// The backend never returns the CRD's literal "Coin/Balance/Point is
// insufficient" string — `RewardService.balanceShortfall` (m21-ng-core-app-service
// app/services/reward.ts) resolves it to whichever resource is actually short:
// "Coin is insufficient", "Balance is insufficient", "Point is insufficient",
// "Coin and Point are insufficient", or "Balance and Point are insufficient".
// "insufficient" does not appear in any other `manualPurchaseReward` failure
// message (confirmed against every `fail()`/error string in
// `validateManualRedemption` and its helpers), so this substring is precise.
const INSUFFICIENT_BALANCE_KEYWORD = "insufficient";

const CustomDialogTitle = ({ children, onClose }: any) => (
  <DialogTitle sx={{ m: 0, p: 2 }}>
    {children}
    {onClose ? (
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
    ) : null}
  </DialogTitle>
);

interface ManualRedemptionDialogProps {
  open: boolean;
  onClose: () => void;
}

export const ManualRedemptionDialog = ({
  open,
  onClose,
}: ManualRedemptionDialogProps) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const { manualRewardRedeemCodeAsync } = useManualRedemption();
  const [isLoading, setIsLoading] = useState(false);

  const [phoneNumber, setPhoneNumber] = useState("");
  const [debouncedPhoneNumber, setDebouncedPhoneNumber] = useState("");
  const [selectedPlatform, setSelectedPlatform] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const [resolutionState, setResolutionState] =
    useState<ResolutionState>("idle");
  const [resolvedSubscriber, setResolvedSubscriber] =
    useState<ResolvedRewardSubscriber | null>(null);
  const [resolutionError, setResolutionError] = useState("");
  const resolutionTokenRef = useRef(0);

  const [selectedCategory, setSelectedCategory] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [selectedReward, setSelectedReward] = useState<{
    id: string;
    displayName: string;
  } | null>(null);
  const [itemGallery, setItemGallery] = useState<RedemptionItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<RedemptionItem | null>(
    null
  );
  const [isConfirmOpen, setConfirmOpen] = useState(false);

  // Debounce phone-number input before resolving identity.
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedPhoneNumber(phoneNumber.trim());
    }, 300);
    return () => clearTimeout(handle);
  }, [phoneNumber]);

  // Any edit to phone number or platform immediately invalidates a prior
  // resolution and re-disables Category/Reward until a fresh resolve succeeds.
  useEffect(() => {
    setResolutionState("idle");
    setResolvedSubscriber(null);
    setResolutionError("");
    setSelectedCategory(null);
    setSelectedReward(null);
    setItemGallery([]);
    setSelectedItem(null);
  }, [phoneNumber, selectedPlatform]);

  // Resolve the subscriber once both phone number (debounced) and platform are set.
  useEffect(() => {
    if (!debouncedPhoneNumber || !selectedPlatform) {
      return;
    }
    const token = ++resolutionTokenRef.current;
    setResolutionState("loading");
    resolveRewardSubscriber({
      phone_number: debouncedPhoneNumber,
      platform: selectedPlatform.id,
    })
      .then((response) => {
        if (token !== resolutionTokenRef.current) return; // stale response
        const result = response?.resolveRewardSubscriber;
        if (result?.status && result.subscriber) {
          setResolvedSubscriber(result.subscriber);
          setResolutionState("resolved");
          setResolutionError("");
        } else {
          setResolvedSubscriber(null);
          setResolutionState("unresolved");
          setResolutionError(result?.error || "Unable to resolve subscriber");
        }
      })
      .catch((error: any) => {
        if (token !== resolutionTokenRef.current) return; // stale response
        setResolvedSubscriber(null);
        setResolutionState("unresolved");
        setResolutionError(error?.message || "Unable to resolve subscriber");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedPhoneNumber, selectedPlatform]);

  const categoryOptions = useMemo(
    () =>
      getRewardCategoriesForPlatform(selectedPlatform?.id).map((id) => ({
        id,
        name: id,
      })),
    [selectedPlatform]
  );

  // Fetch rewards via GraphQL rewardList with filter/pagination/sort
  const { data: rewardListData, isLoading: isLoadingRewards } =
    useRewardListQuery(
      {
        filter: { category: selectedCategory ? [selectedCategory.id] : [] },
        pagination: { page: 1, perPage: 100 },
        sort: { field: "created_at", order: "DESC" },
        subscriber_id: resolvedSubscriber?.id,
        platform: selectedPlatform?.id,
      },
      { enabled: !!resolvedSubscriber && !!selectedCategory }
    );

  const processedItems = useMemo<RedemptionItem[]>(() => {
    const rewards: RewardItem[] =
      rewardListData?.rewardListByAdmin?.data?.rewards ?? [];
    if (!rewards?.length) return [];

    return rewards.map((item) => {
      const name = item.label || "Unnamed Item";
      const service = item.category || "N/A";
      const points = item.amount?.point ?? 0;
      const coin = item.amount?.coin ?? 0;
      return {
        id: item.id,
        name,
        service,
        displayName: `${name} (${service})`,
        imageUrl:
          item.image ||
          `https://placehold.co/600x400/e2e8f0/475569?text=No+Image`,
        points: typeof points === "number" ? points : 0,
        balance: typeof coin === "number" ? coin : 0, // treat coin as balance
        stock: item.stock ?? null,
        available: item.available ?? null,
      };
    });
  }, [rewardListData]);

  const rewardDropdownOptions = useMemo(() => {
    return Array.from(
      new Set(
        processedItems.map((item) => ({
          id: item.id,
          displayName: item.displayName,
        }))
      )
    );
  }, [processedItems]);

  useEffect(() => {
    if (selectedReward) {
      const items =
        processedItems?.filter(
          (item) => item.displayName === selectedReward.displayName
        ) || [];
      setItemGallery(items);
      setSelectedItem(null); // Reset item selection when reward changes
    } else {
      setItemGallery([]);
    }
  }, [selectedReward, processedItems]);

  const handleClose = () => {
    setPhoneNumber("");
    setDebouncedPhoneNumber("");
    setSelectedPlatform(null);
    setResolutionState("idle");
    setResolvedSubscriber(null);
    setResolutionError("");
    setSelectedCategory(null);
    setSelectedReward(null);
    setSelectedItem(null);
    setItemGallery([]);
    onClose();
  };

  const handleRedeemClick = () => {
    setConfirmOpen(true);
  };

  const handleFinalConfirm = async () => {
    if (
      resolutionState !== "resolved" ||
      !resolvedSubscriber ||
      !selectedPlatform ||
      !selectedReward ||
      !selectedItem
    )
      return;

    setIsLoading(true);
    setConfirmOpen(false);

    try {
      const input = {
        reward_catalog_id: selectedReward.id,
        platform: selectedPlatform.id,
        phone_number: debouncedPhoneNumber,
      };

      const response = await manualRewardRedeemCodeAsync(input);

      // Check if the response indicates an error
      if (response?.manualPurchaseReward) {
        const { status, error } = response.manualPurchaseReward;

        if (!status && error) {
          const isInsufficientBalance = error
            .toLowerCase()
            .includes(INSUFFICIENT_BALANCE_KEYWORD);
          if (isInsufficientBalance) {
            notify(error, { type: "error" });
          } else {
            notify(`Redemption failed: ${error}`, { type: "error" });
          }
          return;
        }
      }

      notify("Redemption successful!", { type: "success" });
      handleClose();
      refresh();
    } catch (error) {
      notify("Redemption failed. Please try again.", { type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleItemClick = (item: RedemptionItem) => {
    if (item.available === false || item.stock === 0) return;
    if (selectedItem?.id === item.id) {
      setSelectedItem(null);
    } else {
      setSelectedItem(item);
    }
  };

  const isResolved = resolutionState === "resolved";

  return (
    <>
      <Dialog onClose={handleClose} open={open} fullWidth maxWidth="md">
        <CustomDialogTitle onClose={handleClose}>
          Manual Order Redemption
        </CustomDialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            {/* Left Column: Inputs */}
            <Grid item xs={12} md={5}>
              <Box display="flex" flexDirection="column" gap={3}>
                <Box>
                  <TextField
                    label="Phone Number"
                    placeholder="Enter phone number"
                    fullWidth
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                  />
                  {resolutionState === "loading" && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 0.5, display: "block" }}
                    >
                      Resolving subscriber...
                    </Typography>
                  )}
                  {resolutionState === "resolved" && resolvedSubscriber && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      <Box component="span">{resolvedSubscriber.name}</Box>
                      {" — "}
                      <Box component="span">{resolvedSubscriber.phone}</Box>
                    </Typography>
                  )}
                  {resolutionState === "unresolved" && (
                    <Typography
                      variant="body2"
                      color="error"
                      sx={{ mt: 0.5 }}
                    >
                      {resolutionError}
                    </Typography>
                  )}
                </Box>
                <Autocomplete
                  options={PLATFORM_OPTIONS}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  value={selectedPlatform}
                  onChange={(_, newValue) => setSelectedPlatform(newValue)}
                  renderInput={(params) => (
                    <TextField {...params} label="Platform" />
                  )}
                />
                <Autocomplete
                  options={categoryOptions}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  disabled={!isResolved}
                  value={selectedCategory}
                  onChange={(_, newValue) => {
                    setSelectedCategory(newValue);
                    setSelectedReward(null);
                    setItemGallery([]);
                    setSelectedItem(null);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Category" />
                  )}
                />
                <Autocomplete
                  options={rewardDropdownOptions}
                  getOptionLabel={(option) => option.displayName}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  loading={isLoadingRewards}
                  disabled={!isResolved || !selectedCategory}
                  value={selectedReward}
                  onChange={(_, newValue) => {
                    setSelectedReward(newValue);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Reward" />
                  )}
                />
              </Box>
            </Grid>
            {/* Right Column: Item Gallery */}
            <Grid item xs={12} md={7}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                {selectedReward?.id
                  ? `Select an item`
                  : "Select a reward to see items"}
              </Typography>
              <Grid container spacing={2}>
                {itemGallery.map((item) => {
                  const outOfStock = item.available === false || item.stock === 0;
                  return (
                    <Grid item key={item.id} xs={12} sm={4}>
                      <Card
                        variant="outlined"
                        sx={{
                          borderColor:
                            selectedItem?.id === item.id
                              ? "primary.main"
                              : "divider",
                          borderWidth: 2,
                          position: "relative",
                          opacity: outOfStock ? 0.5 : 1,
                        }}
                      >
                        <CardActionArea
                          disabled={outOfStock}
                          onClick={() => handleItemClick(item)}
                        >
                          <CardMedia
                            component="img"
                            height="120"
                            image={item.imageUrl}
                            alt={item.name}
                            onError={(e: any) => {
                              e.target.onerror = null;
                              e.target.src =
                                "https://placehold.co/600x400?text=Error";
                            }}
                          />
                          <CardContent sx={{ textAlign: "center", p: 1 }}>
                            <Typography variant="body2" component="div">
                              {item.displayName}
                              {outOfStock ? " (Out of stock)" : ""}
                            </Typography>
                          </CardContent>
                          {selectedItem?.id === item.id && (
                            <CheckCircleIcon
                              color="primary"
                              sx={{
                                position: "absolute",
                                top: 8,
                                right: 8,
                                backgroundColor: "white",
                                borderRadius: "50%",
                              }}
                            />
                          )}
                        </CardActionArea>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRedeemClick}
            disabled={
              !isResolved ||
              !selectedCategory ||
              !selectedReward ||
              !selectedItem ||
              isLoading
            }
            sx={{ minWidth: 120 }}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Redeem"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Second confirmation dialog */}
      <Confirm
        isOpen={isConfirmOpen}
        loading={isLoading}
        title="Confirm Redemption"
        content={
          <Box>
            <Typography gutterBottom>You are about to redeem for:</Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Phone Number:</strong> {resolvedSubscriber?.phone} (
              {resolvedSubscriber?.name})
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Item:</strong> {selectedItem?.displayName}
            </Typography>
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                backgroundColor: "grey.100",
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                <strong>Points Deducted:</strong>{" "}
                {selectedItem?.points.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Balance Deducted:</strong>{" "}
                {selectedItem?.balance.toLocaleString()}
              </Typography>
            </Box>
          </Box>
        }
        onConfirm={handleFinalConfirm}
        onClose={() => setConfirmOpen(false)}
        confirm="Confirm"
        cancel="Cancel"
      />
    </>
  );
};
